import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yeshuman/sdk'
import { useState, useEffect } from 'react'
import { Symptom, CreateSymptomData, UpdateSymptomData } from '../hooks/use-symptoms'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface SymptomFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  symptom?: Symptom | null
  onCreate: (data: CreateSymptomData) => Promise<void>
  onUpdate: (data: UpdateSymptomData) => Promise<void>
}

export function SymptomFormDialog({
  open,
  onOpenChange,
  symptom,
  onCreate,
  onUpdate,
}: SymptomFormDialogProps) {
  const [symptomTypeName, setSymptomTypeName] = useState('')
  const [anatomyName, setAnatomyName] = useState('')
  const [severity, setSeverity] = useState<'mild' | 'moderate' | 'severe' | 'very_severe'>('mild')
  const [occurredAt, setOccurredAt] = useState('')
  const [notes, setNotes] = useState('')
  const [anatomyMedicalTerm, setAnatomyMedicalTerm] = useState('')
  const [anatomyParentName, setAnatomyParentName] = useState('')
  
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form when symptom is provided (edit mode)
  useEffect(() => {
    if (symptom) {
      setSymptomTypeName(symptom.symptom_type)
      setAnatomyName(symptom.anatomy.name)
      setSeverity(symptom.severity)
      // Format datetime for input (YYYY-MM-DDTHH:mm)
      const date = new Date(symptom.occurred_at)
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
      setOccurredAt(localDateTime)
      setNotes(symptom.notes || '')
      setAnatomyMedicalTerm(symptom.anatomy.medical_term || '')
      setAnatomyParentName(symptom.anatomy.parent || '')
    } else {
      // Reset form for create mode
      const now = new Date()
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
      setOccurredAt(localDateTime)
      setSymptomTypeName('')
      setAnatomyName('')
      setSeverity('mild')
      setNotes('')
      setAnatomyMedicalTerm('')
      setAnatomyParentName('')
    }
  }, [symptom, open])

  const formatDateTimeForAPI = (dateTimeStr: string): string => {
    // Convert local datetime to ISO string
    const date = new Date(dateTimeStr)
    return date.toISOString()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      if (symptom) {
        // Update mode
        const updateData: UpdateSymptomData = {
          symptom_id: symptom.id,
        }

        if (symptomTypeName) updateData.symptom_type_name = symptomTypeName
        if (anatomyName) updateData.anatomy_name = anatomyName
        if (severity) updateData.severity = severity
        if (occurredAt) updateData.occurred_at = formatDateTimeForAPI(occurredAt)
        if (notes !== undefined) updateData.notes = notes || undefined
        if (anatomyMedicalTerm) updateData.anatomy_medical_term = anatomyMedicalTerm
        if (anatomyParentName) updateData.anatomy_parent_name = anatomyParentName

        await onUpdate(updateData)
        toast.success('Symptom updated successfully')
      } else {
        // Create mode
        if (!symptomTypeName) {
          toast.error('Symptom type is required')
          setIsSubmitting(false)
          return
        }
        if (!anatomyName) {
          toast.error('Body location is required')
          setIsSubmitting(false)
          return
        }
        if (!occurredAt) {
          toast.error('Occurred at time is required')
          setIsSubmitting(false)
          return
        }

        const createData: CreateSymptomData = {
          symptom_type_name: symptomTypeName,
          anatomy_name: anatomyName,
          severity,
          occurred_at: formatDateTimeForAPI(occurredAt),
          notes: notes || undefined,
          anatomy_medical_term: anatomyMedicalTerm || undefined,
          anatomy_parent_name: anatomyParentName || undefined,
        }

        await onCreate(createData)
        toast.success('Symptom logged successfully')
      }

      onOpenChange(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to save symptom')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{symptom ? 'Edit Symptom' : 'Log Symptom'}</DialogTitle>
          <DialogDescription>
            {symptom ? 'Update your symptom details' : 'Record a new health symptom'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="symptom-type">Symptom Type *</Label>
            <Input
              id="symptom-type"
              placeholder="e.g., headache, fatigue, nausea, dizziness"
              value={symptomTypeName}
              onChange={(e) => setSymptomTypeName(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Enter the type of symptom you're experiencing
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="anatomy-name">Body location *</Label>
            <Input
              id="anatomy-name"
              placeholder="e.g., head, chest, back, forehead"
              value={anatomyName}
              onChange={(e) => setAnatomyName(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Where on your body is the symptom located?
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="anatomy-parent">Parent location (optional)</Label>
              <Input
                id="anatomy-parent"
                placeholder="e.g., head (for forehead)"
                value={anatomyParentName}
                onChange={(e) => setAnatomyParentName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                For hierarchical locations (e.g., forehead → head)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="anatomy-medical-term">Medical term (optional)</Label>
              <Input
                id="anatomy-medical-term"
                placeholder="e.g., cranium, thorax"
                value={anatomyMedicalTerm}
                onChange={(e) => setAnatomyMedicalTerm(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Anatomical/medical terminology
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="severity">Severity *</Label>
            <Select
              value={severity}
              onValueChange={(value: 'mild' | 'moderate' | 'severe' | 'very_severe') => setSeverity(value)}
            >
              <SelectTrigger id="severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mild">Mild</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="severe">Severe</SelectItem>
                <SelectItem value="very_severe">Very severe</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="occurred-at">Occurred at *</Label>
            <Input
              id="occurred-at"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              When did this symptom occur?
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Additional details about the symptom..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {symptom ? 'Update Symptom' : 'Log Symptom'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

